import { set } from "radash"

import { httpPost } from "../http"

export interface GeocodeV4Body {
  location?: {
    longitude: number
    latitude: number
  }
  longitude?: number
  latitude?: number
}

export interface GeocodeV4Response {
  address_full: Addressfull
  address: string
  status: string
  dev_message: string
  cache: boolean
  source: string
  code: number
  location_of_interest?: any
  in_polygon: boolean
  polygon: Polygon
}

interface Polygon {
  description: string
  structured_formatting: Structuredformatting
  coordinates: Location[]
}

interface Structuredformatting {
  main_text: string
  secondary_text: string
}

interface Addressfull {
  results: GeocodeResult[]
  children: any[]
  status: string
}

export interface GeocodeResult {
  formatted_address: string
  place_id: string
  geometry: Geometry
  address_components: Addresscomponent[]
  types: string[]
  type: string
  children: any[]
}

interface Addresscomponent {
  long_name: string
  types: any[]
}

interface Geometry {
  location: Location
  location_type: string
}

interface Location {
  lat: number
  lng: number
}

export async function v4CustomersGeocode(
  data: GeocodeV4Body
): Promise<GeocodeV4Response> {
  const response = await httpPost<GeocodeV4Response>(
    "be-map-services/v4/customers/geocode",
    data
  )
  return response.data
}

export interface V1PlacesAutocompleteBody {
  target: string
  text_input: string
}

export interface PlaceAutocompleteChildren {
  description: string
  place_id: string
  structured_formatting: StructuredFormatting
  matched_substrings: any[]
  had_children: boolean
  distance_meters: number
}

export interface PlaceAutocomplete {
  description: string
  place_id: string
  structured_formatting: StructuredFormatting
  matched_substrings: any[]
  children: PlaceAutocompleteChildren[]
  had_children: boolean
  distance_meters: number
  source: string
}

export interface StructuredFormatting {
  main_text: string
  secondary_text: string
  main_text_matched_substrings: any[]
}

export async function v1CustomersPlaceAutocomplete(
  data: V1PlacesAutocompleteBody,
  clearLocation = false
) {
  const clonedData = { ...data }

  if (clearLocation) {
    set(clonedData, "location", undefined)
    set(clonedData, "latitude", undefined)
    set(clonedData, "longitude", undefined)
    set(clonedData, "client_info.location", undefined)
    set(clonedData, "client_info.latitude", undefined)
    set(clonedData, "client_info.longitude", undefined)
  }

  const response = await httpPost(
    "be-map-services/v1/customers/places/autocomplete",
    clonedData
  )
  return response.data
}

export interface V1CustomersPlaceDetailBody {
  place_id: string
  target: "pick_up" | "drop_off"
}

export interface V1CustomersPlaceDetailResponse {
  code: number
  result: Result
  status: string
  dev_message: string
  cache: boolean
  source: string
  location_of_interest?: any
}

interface Result {
  formatted_address: string
  geometry: Geometry
  name: string
  status: string
  vehicle: string
  vehicle_ids: any[]
}

export async function v1CustomersPlaceDetail(
  body: V1CustomersPlaceDetailBody
): Promise<V1CustomersPlaceDetailResponse> {
  const resp = await httpPost<V1CustomersPlaceDetailResponse>(
    "be-map-services/v1/customers/places/details",
    body
  )
  return resp.data
}

export interface V2DirectionMultiVehiclesBody {
  origin: {
    longitude: number
    latitude: number
  }
  destination: {
    longitude: number
    latitude: number
  }
  phase: string
  purpose: string
}

export interface V2DirectionMultiVehiclesResponse {
  vehicle_direction: Vehicledirection[]
  status: string
  dev_message: string
  cache: boolean
  source: string
  code: number
}

interface Vehicledirection {
  vehicle: string
  direction: Direction
  vehicle_ids: number[]
}

interface Direction {
  routes: Route[]
  status: string
}

interface Route {
  legs: Leg[]
  overview_polyline: Polyline
  separated_polyline: Polyline[]
  bounds: Bounds
}

interface Bounds {
  northeast?: any
  southwest?: any
}

interface Leg {
  steps: Step[]
  distance: Distance
  duration: Distance
  end_address: string
  end_location: Endlocation
  start_address: string
  start_location: Endlocation
  original_duration?: any
}

interface Step {
  distance: Distance
  duration: Distance
  end_location: Endlocation
  start_location: Endlocation
  polyline: Polyline
  travel_mode: string
}

interface Polyline {
  points: string
  color: string
}

interface Endlocation {
  lat: number
  lng: number
}

interface Distance {
  value: number
  text: string
}

export async function v2DirectionMultiVehicles(
  body: V2DirectionMultiVehiclesBody
): Promise<V2DirectionMultiVehiclesResponse> {
  const resp = await httpPost<V2DirectionMultiVehiclesResponse>(
    "be-maps/v2/customers/directions_multi_vehicle",
    body
  )
  return resp.data
}

export interface V2CustomerDirectionsBody {
  origin: {
    longitude: number
    latitude: number
  }
  destination: {
    longitude: number
    latitude: number
  }
  phase: string
  purpose: string
  vehicle_type: number
}

export interface V2CustomerDirectionsResponse {
  direction: Direction
  status: string
  dev_message: string
  cache: boolean
  source: string
  code: number
  vehicle: string
}

export async function v2CustomerDirections(
  body: V2CustomerDirectionsBody
): Promise<V2CustomerDirectionsResponse> {
  const resp = await httpPost<V2CustomerDirectionsResponse>(
    "be-map-services/v2/customers/directions",
    body
  )
  return resp.data
}
